import React from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import useSWRMutation from 'swr/mutation';
import { RouterInput, trpc } from '../../../../api/trpc';
import { AlertService } from '../../../../services/alert.service';
import { useModalClose } from '../../../../utils/useModalClose';
import { required } from '../../../../utils/validations';
import { InputWrapper } from '../../../common/input-wrapper';
import { OtcommerceTagsSelector } from '../../../common/otcommerce-tags-selector';

type Form = {
  caption: string;
  tags: { value: string }[];
  value: string;
};

export type ModalProps = {
  close: () => void;
  reject: () => void;
  item?: Form & { id: number };
};

export const AddRecommendationUtmModal: React.FC<ModalProps> = ({
  close,
  reject,
  item,
}) => {
  const {
    formState: { isSubmitting },
    handleSubmit,
    control,
  } = useForm<Form>({
    defaultValues: item,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'tags',
  });

  const { trigger: triggerUpdate } = useSWRMutation(
    ['recommendationUtmAdmin.update'],
    (_k, { arg }: { arg: RouterInput['recommendationUtmAdmin']['update'] }) =>
      trpc.recommendationUtmAdmin.update.mutate(arg),
  );
  const { trigger: triggerCreate } = useSWRMutation(
    ['recommendationUtmAdmin.create'],
    (_k, { arg }: { arg: RouterInput['recommendationUtmAdmin']['create'] }) =>
      trpc.recommendationUtmAdmin.create.mutate(arg),
  );

  const onSubmit = handleSubmit(async (form) => {
    const data = { ...form, tags: form.tags.map((tag) => tag.value) };
    if (item) {
      await triggerUpdate({
        id: item.id,
        data,
      });
    } else {
      await triggerCreate(data);
    }
    AlertService.success();
    close();
  });

  const modalRef = useModalClose(reject);

  return (
    <div className="modal-dialog" ref={modalRef}>
      <form className="modal-content" onSubmit={onSubmit}>
        <div className="modal-header border-bottom-0 p-4">
          <h3>{item ? 'Редактировать' : 'Добавить'} utm метку</h3>
          <button
            type="button"
            className="btn-close"
            onClick={() => reject()}
            disabled={isSubmitting}
          />
        </div>
        <div className="modal-body p-4">
          <Controller
            control={control}
            name="caption"
            rules={{
              ...required,
            }}
            render={({ field, fieldState }) => (
              <InputWrapper error={fieldState.error?.message} required>
                <div className={'d-flex mb-3 gap-2 '}>
                  <div
                    className={'align-self-center text-end'}
                    style={{ width: 90 }}
                  >
                    {' '}
                    utm_
                  </div>
                  <input className="form-control " type="text" {...field} />
                </div>
              </InputWrapper>
            )}
          />
          <Controller
            control={control}
            name="value"
            rules={{
              ...required,
            }}
            render={({ field, fieldState }) => (
              <InputWrapper error={fieldState.error?.message} required>
                <div className={'d-flex mb-3 gap-2 '}>
                  <div
                    className={'align-self-center  text-end'}
                    style={{ width: 90 }}
                  >
                    {' '}
                    значение
                  </div>
                  <input className="form-control " type="text" {...field} />
                </div>
              </InputWrapper>
            )}
          />
          <div className={'py-2 border-bottom'}>
            <div className={'d-flex gap-2 '}>
              <div>Теги</div>
            </div>

            <div className={'d-flex flex-wrap d-flex gap-2'}>
              {fields.map((field, index) => {
                return (
                  <div className={'d-flex gap-2 pb-2'} key={field.id}>
                    <Controller
                      control={control}
                      name={`tags.${index}`}
                      render={({ field }) => {
                        return (
                          <div
                            className={
                              'border-gray-200 border p-2 rounded d-flex hover-bg-light cursor-pointer'
                            }
                            onClick={() => {
                              remove(index);
                            }}
                          >
                            {field.value.value}
                            <div className=" justify-content-center">
                              <i className="bi bi-x" />
                            </div>
                          </div>
                        );
                      }}
                    />
                  </div>
                );
              })}
            </div>

            <OtcommerceTagsSelector
              onAppend={(value) => {
                append({ value: value });
              }}
            />
          </div>
        </div>
        <div className="modal-footer justify-content-end">
          <button
            type="submit"
            className="btn btn-success"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
            ) : item ? (
              'Сохранить'
            ) : (
              'Добавить'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
